<template>
  <VueDatePicker
    v-bind="{...$attrs, enableTimePicker: false}"
    range
    multi-calendars
    :auto-apply="false"
    :format="formatDate"
    :preview-format="formatDate"
    :clearable="false"
    :preset-dates="presetDates"
    v-model="value"
    @update:model-value="onDateChange"
  >
    <template #dp-input>
      <input
        class="dp__input dp__input_icon_pad"
        :value="formattedInputValue"
        readonly
      >
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import { SHORTCUTS } from '.'

const props = defineProps({
  initialValue: {
    type: Object,
    default: () => {},
    required: false,
  },
  confirm: {
    type: Function,
    default: null,
    required: false
  },
})

function formatDate (dates: Date | Date[] | string | string[]): string {
  const fmt = (d: Date | string) => {
    const date = d instanceof Date ? d : new Date(d)
    if (isNaN(date.getTime())) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }
  if (Array.isArray(dates)) {
    return dates.filter(Boolean).map(fmt).join(' - ')
  }
  return dates ? fmt(dates) : ''
}

const formattedInputValue = computed(() => {
  if (!value.value) return ''
  return formatDate(value.value)
})

const shortcut = ref(props.initialValue.shortcut)
const value = ref(props.initialValue.value)
const presetDates = Object.entries(SHORTCUTS)
  .map(([, {text, start, end}]) => ({
    label: text,
    value: [start, end],
  }))

function stripTime (d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function onDateChange (val) {
  value.value = val
  // Check if the selected range matches a preset/shortcut
  shortcut.value = null
  if (val && val.length === 2) {
    for (const [key, { start, end }] of Object.entries(SHORTCUTS)) {
      if (
        val[0] && val[1] &&
        stripTime(new Date(val[0])).getTime() === stripTime(new Date(start)).getTime() &&
        stripTime(new Date(val[1])).getTime() === stripTime(new Date(end)).getTime()
      ) {
        shortcut.value = key
        break
      }
    }
  }
  if (props.confirm) {
    props.confirm({shortcut: shortcut.value, value: value.value})
  }
}

// created lifecycle equivalent
if (shortcut.value) {
  const {start, end} = SHORTCUTS[shortcut.value]
  value.value = [start, end]
  if (props.confirm) {
    props.confirm({shortcut: shortcut.value, value: value.value})
  }
}
</script>

<style>
  /* Customisation to the datepicker package css */

  /* Input — give the text enough horizontal padding to clear the
     clear (×) icon on the right, otherwise long ranges like
     "01/05/2025 - 30/04/2026" overlap with the icon and the last
     digit gets clipped. */
  .dp__input,
  .dp__input.dp__input_icon_pad {
    padding-right: 2.25rem;
    min-width: 16rem;
  }

  .dp__action_button {
    color: #fff !important;
    background-color: #20a8d8 !important;
    border-color: #20a8d8 !important;
  }

  .dp__action_button:hover {
    color: #fff !important;
    background-color: #1b8eb7 !important;
    border-color: #1985ac !important;
  }

  /* Hide time picker elements */
  .dp__time_display,
  .dp__overlay_container--tp,
  .dp--tp-wrap,
  .dp__btn[data-test="open-time-picker-btn"],
  .dp__time_input {
    display: none !important;
  }

  /* Preset dates displayed as horizontal pills */
  .dp__preset_ranges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
    border-bottom: 1px solid #ddd;
    width: 100%;
  }

  .dp__preset_range {
    padding: 0.25rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.8rem;
    cursor: pointer;
    color: #636E72;
    white-space: nowrap;
  }

  .dp__preset_range:hover {
    background-color: #20a8d8;
    color: #fff;
  }
</style>
